import { Button } from "@/components/elements/Button";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { EXTERNAL_ID_KEY } from "@/constants/common";
import { DESCRIPTION_LINES, LOGIN_CACHED_EXTERNAL_ID } from "@/features/auth/constant/login";
import { usePasskeyLogin } from "@/features/auth/hooks/usePasskeyLogin";
import { loginSchema } from "@/features/auth/schema/loginSchema";
import { TLoginForm } from "@/features/auth/types/loginForm";
import { isPasskeySupported } from "@/features/auth/utils/passkey";
import { useCacheLocal } from "@/hooks/useCacheLocal";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { createUrlWithParams, getQueryFromURL } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import KeyIcon from "@mui/icons-material/Key";
import LoginIcon from "@mui/icons-material/Login";
import { Box, Divider, Paper, Stack, Typography, styled } from "@mui/material";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

export const LoginForm = () => {
  const cachedExternalIdStore = useCacheLocal(LOGIN_CACHED_EXTERNAL_ID);
  const isAlreadyCache = cachedExternalIdStore.isExist();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [remember, setRemember] = useState(true);
  const { isPasskeyLoading, handlePasskeyLogin } = usePasskeyLogin({ autoStart: true });
  const passkeyEnabled = useMemo(() => isPasskeySupported(), []);

  const cachedExternalId = useMemo(() => {
    if (!isAlreadyCache) return "";
    const externalId = cachedExternalIdStore.get();
    if (typeof externalId === "string") return externalId;
    return "";
  }, [cachedExternalIdStore, isAlreadyCache]);

  const externalId = useMemo(() => {
    if (searchParams.has(EXTERNAL_ID_KEY)) return searchParams.get(EXTERNAL_ID_KEY) as string;
    return cachedExternalId;
  }, [searchParams, cachedExternalId]);

  const methods = useForm<TLoginForm>({
    defaultValues: {
      externalId,
    },
    mode: "onChange",
    resolver: yupResolver(loginSchema),
  });

  const { control } = methods;

  const onSubmit = async (data: TLoginForm) => {
    if (remember) {
      cachedExternalIdStore.set(data.externalId);
    } else {
      cachedExternalIdStore.remove();
    }
    const query = getQueryFromURL();
    navigate(createUrlWithParams(AppRoutes.loginCredentials, { ...query, externalId: data.externalId }));
  };

  const handleToggleCache = (_: unknown, checked: boolean) => {
    setRemember(checked);
  };

  return (
    <Container>
      <Form methods={methods} onSubmit={onSubmit} enableBlocker={false} schema={loginSchema}>
        {passkeyEnabled && (
          <Stack>
            <Button size="lg" variant="primary" fullWidth onClick={handlePasskeyLogin} isLoading={isPasskeyLoading}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                <KeyIcon sx={{ fontSize: 20 }} />
                <Typography variant="sub16Semi">パスキーでログイン</Typography>
              </Stack>
            </Button>
            <Box my="32px" py="20px">
              <Divider>
                <Typography variant="body14" color={text.secondary}>
                  または組織IDを入力してログイン
                </Typography>
              </Divider>
            </Box>
          </Stack>
        )}
        <Stack gap="28px" width={400}>
          <Stack>
            {DESCRIPTION_LINES.map((line) => (
              <Typography key={line} variant="cap12" color={text.secondary}>
                {line}
              </Typography>
            ))}
          </Stack>

          <Stack flexDirection="column" gap={2}>
            <Stack flexDirection="column" gap="12px">
              <FormField control={control} name="externalId" label="組織ID" required>
                <Input size="lg" placeholder="入力してください" />
              </FormField>
              <Checkbox onChange={handleToggleCache} checked={remember} label="組織IDをWebブラウザに保存する" />
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
              <Button type="submit" variant="outline" size="lg" fullWidth>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                  <LoginIcon fontSize="small" />
                  <Typography variant="body14" fontWeight="500">
                    ログインへ移る
                  </Typography>
                </Stack>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Form>
    </Container>
  );
};

const Container = styled(Paper)`
  border-radius: 12px;
  padding: 32px;
  box-shadow:
    0 4px 4px -1px rgba(12, 12, 13, 0.05),
    0 4px 4px -1px rgba(12, 12, 13, 0.1) !important;
`;
